// 🎯 Citation-driven ParagraphOverlay highlighting (alternative to regex text highlighting).
// Fully self-contained: targets overlay boxes by their additive `data-kgnf-id` attribute and
// toggles a dedicated highlight class. Does NOT touch the existing regex highlight path.
import { scrollToPageDefault, scrollToPageNoSmooth } from "../../scrll_glob";
import styles from "./ParagraphOverlay.module.css";

const boxSelector = (kgnfId: number) => `div.${styles.paragraphBox}[data-kgnf-id="${kgnfId}"]`;

// remove the citation highlight from every overlay box
export const clearCitationHighlights = () => {
    const boxes = document.querySelectorAll<HTMLElement>(`div.${styles.paragraphBox}.${styles.citationHighlight}`);
    boxes.forEach(box => box.classList.remove(styles.citationHighlight));
};

// apply the citation highlight to the boxes matching the given kgnf ids (clears others first)
export const highlightParagraphsByIds = (kgnfIds: number[]): number => {
    clearCitationHighlights();
    let shown = 0;
    for (const id of kgnfIds) {
        const boxes = document.querySelectorAll<HTMLElement>(boxSelector(id));
        boxes.forEach(box => {
            box.classList.add(styles.citationHighlight);
            shown++;
        });
    }
    return shown;
};

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// wait until the overlay box for `kgnfId` is mounted (page lazily rendered), scrolling toward it
const waitForBoxMounted = async (kgnfId: number, page1Based: number, timeoutMs: number): Promise<boolean> => {
    const deadline = Date.now() + timeoutMs;
    let firstPass = true;
    while (Date.now() < deadline) {
        const box = document.querySelector<HTMLElement>(boxSelector(kgnfId));
        if (box) return true;
        // nudge the lazy-loader: enable user-scroll lazy load and scroll toward the page
        window.globalUserScrollLazyLoadEnabled = true;
        if (firstPass) {
            scrollToPageNoSmooth(page1Based, "‹para_hl›");
            firstPass = false;
        } else {
            scrollToPageDefault(page1Based);
        }
        await sleep(120);
    }
    return !!document.querySelector<HTMLElement>(boxSelector(kgnfId));
};

export interface scrollAndHighlightOpts {
    timeoutMs?: number;      // max wait for lazy page mount (default 3500ms)
    scrollIntoView?: boolean; // center the first highlighted box after showing (default true)
}

// scroll to the resolved page (loading it if needed) and highlight the resolved paragraph boxes.
// returns true if at least one box was highlighted.
export const scrollAndHighlightParagraphs = async (
    kgnfIds: number[],
    pages1Based: number[],
    opts: scrollAndHighlightOpts = {}
): Promise<boolean> => {
    if (!kgnfIds || kgnfIds.length === 0) return false;
    const timeoutMs = opts.timeoutMs ?? 3500;
    const firstId = kgnfIds[0];
    const firstPage = pages1Based?.[0] || 1;

    const mounted = await waitForBoxMounted(firstId, firstPage, timeoutMs);
    // highlight whatever is currently mounted (boxes on not-yet-mounted pages light up when scrolled into view)
    const shown = highlightParagraphsByIds(kgnfIds);

    if ((opts.scrollIntoView ?? true) && mounted) {
        const box = document.querySelector<HTMLElement>(boxSelector(firstId));
        if (box) box.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    return shown > 0;
};
